import React from 'react';
import styled from '@emotion/styled';

import { fontWeights } from '../../../theme/type';
import { body } from '../../../styles/type';
import { colors } from '../../../theme/colors';
import { rem } from '../../../utilities/style';
import { media } from '../../../theme/media';

import DownloadModule from './DownloadModule';
import { ReactComponent as IconWindows } from '../../../images/icons/os-windows.svg';
import { ReactComponent as IconApple } from '../../../images/icons/os-apple.svg';
import { ReactComponent as IconChrome } from '../../../images/icons/os-chrome.svg';
import { ReactComponent as IconLinux } from '../../../images/icons/os-linux.svg';
import { ReactComponent as IconMobile } from '../../../images/icons/os-mobile.svg';

const Container = styled.div`
  overflow: hidden;
  height: 0;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in;

  ${({ activeTab }) => activeTab && `
    height: auto;
    opacity: 1;
    visibility: visible;
  `}
`;

const Accordion = styled.details`
  border-bottom: 1px solid ${colors.gray['200']};
`;

const AccordionHeader = styled.summary`
  list-style: none;
  display: flex;
  align-items: center;
  padding: ${rem(16)} 0;
  font-size: ${rem(16)};
  font-weight: ${fontWeights.medium};
  line-height: 1.2;
  color: ${colors.blue['800']};
  cursor: pointer;

  ${media('>tiny')} {
    padding: ${rem(24)} 0;
    font-size: ${rem(20)};
  }

  &::-webkit-details-marker {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    width: 1px;
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
  }

  svg {
    margin-right: ${rem(16)};

    ${Accordion}[open] & {
      color: ${colors.blue['500']};
    }
  }

  &::after {
    content: '';
    display: block;
    margin-left: auto;
    border: ${rem(4)} solid transparent;
    border-top: ${rem(6)} solid ${colors.blue['800']};
    border-bottom-width: 0;

    ${Accordion}[open] & {
      transform: scaleY(-1);
      border-top-color: ${colors.blue['500']};
    }
  }
`;

const AccordionContent = styled.div`
  ${body.regular}
  padding-bottom: ${rem(24)};

  &[aria-hidden="true"] {
    visibility: hidden;
  }
`;

const DownloadTab = ({
  activeTab,
  currentOs,
  setCurrentOs,
  data,
  downloadSections,
  productName,
  currentTab
}) => {
  const changeOs = (event, accordionIndex) => {
    event.preventDefault();

    return currentOs === accordionIndex ? null : accordionIndex;
  };

  return (
    <Container activeTab={activeTab}>
      {data.map(({ os, title, downloads }, i) => (
        <Accordion
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          open={currentOs === os}
        >
          <AccordionHeader onClick={(event) => setCurrentOs(changeOs(event, os))}>
            {os === 'windows' && <IconWindows aria-hidden="true" />}
            {os === 'mac' && <IconApple aria-hidden="true" />}
            {os === 'chromebook' && <IconChrome aria-hidden="true" />}
            {os === 'linux' && <IconLinux aria-hidden="true" />}
            {os === 'mobile' && <IconMobile aria-hidden="true" />}
            {title}
          </AccordionHeader>
          <AccordionContent
            aria-hidden={currentOs !== os}
          >
            {downloads.map(({
              product,
              text,
              disclaimer,
              primaryButtonText,
              primaryButtonLink,
              secondaryButtonText,
              secondaryButtonLink,
              dropdownButton,
              appleStoreLink,
              googleStoreLink,
              amazonStoreLink,
              microsoftStoreLink,
            }, j) => (
              <DownloadModule
                // eslint-disable-next-line react/no-array-index-key
                key={j}
                os={os}
                product={product}
                text={text}
                disclaimer={disclaimer}
                primaryButtonText={primaryButtonText}
                primaryButtonLink={primaryButtonLink}
                secondaryButtonText={secondaryButtonText}
                secondaryButtonLink={secondaryButtonLink}
                dropdownButton={dropdownButton}
                appleStoreLink={appleStoreLink}
                googleStoreLink={googleStoreLink}
                amazonStoreLink={amazonStoreLink}
                microsoftStoreLink={microsoftStoreLink}
                downloadSections={downloadSections}
                productName={productName}
                currentTab={currentTab}
              />
            ))}
          </AccordionContent>
        </Accordion>
      ))}
    </Container>
  );
};

export default DownloadTab;
